import { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { useTenantStore } from "../../stores/tenant";
import { useToastStore } from "../../stores/toast";

interface StoredNumber {
  id: string;
  tenantId: string;
  e164: string;
  status: "active" | "released";
}
interface OwnedNumber {
  sid: string;
  e164: string;
  friendlyName: string;
  assigned: boolean;
}
interface AvailableNumber {
  e164: string;
  friendlyName: string;
  locality: string | null;
  region: string | null;
}

export function NumbersList() {
  const tenants = useTenantStore((s) => s.available);
  const push = useToastStore((s) => s.push);

  const [mine, setMine] = useState<StoredNumber[]>([]);
  const [owned, setOwned] = useState<OwnedNumber[]>([]);
  const [available, setAvailable] = useState<AvailableNumber[]>([]);
  const [tenantId, setTenantId] = useState("");
  const [areaCode, setAreaCode] = useState("514");
  const [busy, setBusy] = useState<string | null>(null);

  const tenantName = useMemo(() => {
    const m = new Map(tenants.map((t) => [t.id, t.name]));
    return (id: string) => m.get(id) ?? id;
  }, [tenants]);

  useEffect(() => {
    const first = tenants[0];
    if (first && !tenantId) setTenantId(first.id);
  }, [tenants, tenantId]);

  const loadMine = () => api<StoredNumber[]>("/api/numbers").then(setMine).catch(() => setMine([]));
  const loadOwned = () => api<OwnedNumber[]>("/api/numbers/owned").then(setOwned).catch(() => setOwned([]));

  useEffect(() => {
    loadMine();
    loadOwned();
  }, []);

  const assign = async (n: OwnedNumber) => {
    if (!tenantId) return;
    setBusy(n.sid);
    try {
      await api("/api/numbers/assign", {
        method: "POST",
        body: JSON.stringify({ e164: n.e164, twilioSid: n.sid, tenantId }),
      });
      push("success", `${n.e164} assigné à ${tenantName(tenantId)}`);
      loadMine();
      loadOwned();
    } catch {
      push("danger", "Échec de l'assignation");
    } finally {
      setBusy(null);
    }
  };

  const search = async () => {
    setBusy("search");
    try {
      const list = await api<AvailableNumber[]>(`/api/numbers/available?areaCode=${encodeURIComponent(areaCode)}`);
      setAvailable(list);
    } catch {
      push("danger", "Recherche impossible");
    } finally {
      setBusy(null);
    }
  };

  const buy = async (n: AvailableNumber) => {
    if (!tenantId) return;
    setBusy(n.e164);
    try {
      await api("/api/numbers/purchase", {
        method: "POST",
        body: JSON.stringify({ e164: n.e164, tenantId }),
      });
      push("success", `${n.e164} acheté pour ${tenantName(tenantId)}`);
      setAvailable((prev) => prev.filter((x) => x.e164 !== n.e164));
      loadMine();
    } catch {
      push("danger", "Achat impossible");
    } finally {
      setBusy(null);
    }
  };

  const release = async (id: string) => {
    setBusy(id);
    try {
      await api(`/api/numbers/${id}`, { method: "DELETE" });
      loadMine();
      loadOwned();
    } catch {
      push("danger", "Libération impossible");
    } finally {
      setBusy(null);
    }
  };

  const activeMine = mine.filter((m) => m.status === "active");
  const unassignedOwned = owned.filter((o) => !o.assigned);

  return (
    <div>
      <header className="page-header">
        <div className="page-header__titles">
          <h1 className="page-header__title">Numéros</h1>
          <p className="page-header__subtitle">
            Assigne un numéro Twilio existant ou achètes-en un neuf — le webhook est configuré pour toi.
          </p>
        </div>
      </header>
      <div className="config-form">
        <label style={{ color: "var(--mut, #9aa3b6)", fontSize: "0.8rem" }}>Établissement cible</label>
        <select className="select" value={tenantId} onChange={(e) => setTenantId(e.target.value)}>
          {tenants.map((t) => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
      </div>

      <h2 className="section-title">Numéros assignés</h2>
      <table className="table">
        <thead><tr><th>Numéro</th><th>Établissement</th><th>Statut</th><th></th></tr></thead>
        <tbody>
          {activeMine.length === 0 ? (
            <tr><td colSpan={4} className="table__empty">Aucun numéro assigné.</td></tr>
          ) : (
            activeMine.map((m) => (
              <tr key={m.id}>
                <td>{m.e164}</td>
                <td>{tenantName(m.tenantId)}</td>
                <td>Actif</td>
                <td>
                  <button type="button" className="btn btn--ghost btn--sm" disabled={busy === m.id} onClick={() => void release(m.id)}>
                    Libérer
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      <h2 className="section-title">Assigner un numéro existant (Twilio)</h2>
      <table className="table">
        <thead><tr><th>Numéro</th><th>Nom</th><th></th></tr></thead>
        <tbody>
          {unassignedOwned.length === 0 ? (
            <tr><td colSpan={3} className="table__empty">Aucun numéro libre sur le compte Twilio.</td></tr>
          ) : (
            unassignedOwned.map((n) => (
              <tr key={n.sid}>
                <td>{n.e164}</td>
                <td>{n.friendlyName}</td>
                <td>
                  <button type="button" className="btn btn--primary btn--sm" disabled={!tenantId || busy === n.sid} onClick={() => void assign(n)}>
                    {busy === n.sid ? "…" : "Assigner"}
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      <h2 className="section-title">Acheter un nouveau numéro</h2>
      <div className="config-form">
        <input
          className="input"
          type="text"
          placeholder="Indicatif régional (ex 514)"
          value={areaCode}
          onChange={(e) => setAreaCode(e.target.value.replace(/\D/g, ""))}
          style={{ maxWidth: "12rem" }}
        />
        <button type="button" className="btn btn--primary" disabled={busy === "search"} onClick={() => void search()}>
          {busy === "search" ? "Recherche…" : "Chercher"}
        </button>
      </div>
      {available.length > 0 ? (
        <table className="table">
          <thead><tr><th>Numéro</th><th>Ville</th><th></th></tr></thead>
          <tbody>
            {available.map((n) => (
              <tr key={n.e164}>
                <td>{n.e164}</td>
                <td>{n.locality ?? n.region ?? "—"}</td>
                <td>
                  <button type="button" className="btn btn--primary btn--sm" disabled={!tenantId || busy === n.e164} onClick={() => void buy(n)}>
                    {busy === n.e164 ? "…" : "Acheter"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </div>
  );
}

export default NumbersList;
